import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { marked } from "marked";
import DOMPurify from "dompurify";
import { Lock, FileText, ShieldCheck, LogOut, FolderLock, Download, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startLogin = () => {
  const redirectUrl = window.location.origin + "/docs";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const CONF_COLOR = {
  "public-gated": "text-emerald-400 border-emerald-500/40",
  internal: "text-sky-400 border-sky-500/40",
  confidential: "text-amber-400 border-amber-500/40",
  restricted: "text-rose-400 border-rose-500/40",
};

export default function Docs() {
  const location = useLocation();
  const [status, setStatus] = useState("checking"); // checking | need_login | denied | ready
  const [me, setMe] = useState(null);
  const [docs, setDocs] = useState([]);
  const [active, setActive] = useState(null);
  const [html, setHtml] = useState("");
  const [vault, setVault] = useState([]);
  const [authError, setAuthError] = useState(location.state?.authError || null);
  const loadedDoc = useRef(null);

  const boot = useCallback(async () => {
    try {
      const { data: meData } = await api.get("/docs/me");
      setMe(meData);
      const { data: list } = await api.get("/docs");
      setDocs(list.items || []);
      setStatus("ready");
      if (list.items?.length) openDoc(list.items[0].id);
      try {
        const { data: v } = await api.get("/docs/vault");
        setVault(v.items || []);
      } catch { /* no vault */ }
    } catch (e) {
      const code = e?.response?.status;
      if (code === 401) setStatus("need_login");
      else if (code === 403) setStatus("denied");
      else setStatus("need_login");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) return; // callback handles it
    boot();
  }, [boot]);

  const openDoc = async (id) => {
    if (loadedDoc.current === id) return;
    loadedDoc.current = id;
    setActive(id);
    try {
      const { data } = await api.get(`/docs/content/${id}`);
      setHtml(DOMPurify.sanitize(marked.parse(data.markdown || "")));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open document");
      setHtml("");
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* noop */ }
    setStatus("need_login"); setMe(null); setDocs([]); setHtml("");
  };

  const getVaultArtefact = async (artId) => {
    try {
      const { data } = await api.post(`/docs/vault/${artId}/request`);
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.download_url}`;
      window.open(url, "_blank", "noopener");
      toast.success("Secure link opened (single-use, expires shortly)");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not request artefact");
    }
  };

  if (status === "checking") {
    return <div className="min-h-screen bg-[#0B0B0C] flex items-center justify-center" data-testid="docs-loading">
      <div className="w-10 h-10 rounded-full border-2 border-white/20 border-t-[#7737F6] animate-spin" /></div>;
  }

  if (status === "need_login" || status === "denied") {
    const denied = status === "denied";
    return (
      <div className="min-h-screen bg-[#0B0B0C] text-white flex items-center justify-center px-6" data-testid="docs-gate">
        <div className="max-w-md text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 border border-[#7737F6]/50 mb-6"><Lock className="text-[#A179FA]" /></div>
          <h1 className="ek-heading text-3xl mb-3">Ekselens Knowledge Base</h1>
          <p className="text-zinc-400 text-sm mb-7">
            {denied
              ? "You're signed in, but this account has no documentation access. Ask the IP owner to grant you an audience tier."
              : "This documentation is private. Sign in with your authorised account to continue — there is no public access."}
          </p>
          {authError && <p className="text-amber-400 text-xs mb-4" data-testid="docs-auth-error">{authError}</p>}
          {denied ? (
            <button onClick={logout} className="ek-btn ek-btn-secondary inline-flex items-center gap-2" data-testid="docs-signout-btn"><LogOut size={15} /> Sign out</button>
          ) : (
            <button onClick={startLogin} className="ek-btn ek-btn-primary inline-flex items-center gap-2" data-testid="docs-signin-btn"><ShieldCheck size={15} /> Sign in to continue</button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0B0C] text-zinc-200 flex flex-col" data-testid="docs-portal">
      <header className="border-b border-zinc-800 px-6 py-3 flex items-center justify-between sticky top-0 bg-[#0B0B0C] z-10">
        <div className="flex items-center gap-3">
          <span className="text-[#7737F6] font-semibold tracking-[0.14em] text-sm">EKSELENS</span>
          <span className="text-zinc-500 text-xs uppercase tracking-[0.2em]">Knowledge Base</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[10px] uppercase tracking-[0.16em] px-2 py-0.5 border border-[#7737F6]/40 text-[#A179FA]" data-testid="docs-tier-badge">{me?.docs_tier}</span>
          <span className="text-xs text-zinc-500 hidden sm:inline">{me?.email}</span>
          <button onClick={logout} className="text-zinc-400 hover:text-white" title="Sign out" data-testid="docs-logout-btn"><LogOut size={16} /></button>
        </div>
      </header>

      <div className="flex flex-1 min-h-0">
        <aside className="w-72 shrink-0 border-r border-zinc-800 p-4 overflow-y-auto" data-testid="docs-sidebar">
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-2">Documents</div>
          {docs.map((d) => (
            <button key={d.id} onClick={() => openDoc(d.id)}
              className={`w-full text-left px-2.5 py-2 mb-1 text-[13px] flex items-start gap-2 transition-colors ${active === d.id ? "bg-[#7737F6]/15 text-white" : "text-zinc-400 hover:bg-white/5"}`}
              data-testid={`docs-nav-${d.id}`}>
              <FileText size={13} className="mt-0.5 shrink-0" />
              <span className="min-w-0">
                <span className="block truncate">{d.title}</span>
                <span className={`text-[9px] uppercase tracking-wider border px-1 ${CONF_COLOR[d.confidentiality] || "text-zinc-500 border-zinc-700"}`}>
                  {d.confidentiality}{d.access === "summary" ? " · summary" : ""}
                </span>
              </span>
            </button>
          ))}

          {vault.length > 0 && (
            <div className="mt-6" data-testid="docs-vault">
              <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-2 flex items-center gap-1"><FolderLock size={11} /> NDA Trust Vault</div>
              {vault.map((a) => (
                <div key={a.id} className="flex items-center justify-between gap-2 px-2.5 py-2 text-[12px] text-zinc-400" data-testid={`vault-row-${a.id}`}>
                  <span className="truncate">{a.title}</span>
                  <button onClick={() => getVaultArtefact(a.id)} className="shrink-0 text-[#A179FA] hover:text-white" title="Secure download" data-testid={`vault-download-${a.id}`}><Download size={14} /></button>
                </div>
              ))}
            </div>
          )}
        </aside>

        <main className="flex-1 min-w-0 overflow-y-auto px-8 py-8" data-testid="docs-content">
          {html ? (
            <>
              <div className="flex justify-end mb-4">
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/docs/content/${active}/pdf`}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.14em] border border-[#7737F6]/50 text-[#A179FA] hover:bg-[#7737F6]/15 px-3 py-1.5 transition-colors"
                  data-testid="docs-download-pdf"
                >
                  <Download size={13} /> Download PDF
                </a>
              </div>
              <article className="docs-prose max-w-3xl" dangerouslySetInnerHTML={{ __html: html }} />
            </>
          ) : (
            <div className="text-zinc-600 flex items-center gap-2"><AlertTriangle size={16} /> Select a document.</div>
          )}
        </main>
      </div>
    </div>
  );
}
